import { useEffect, useRef, useState } from "react";

// Polls the debrief endpoint until it's ready, then renders the three-part
// debrief. Renders nothing while absent/failed so the results page stays clean.
// max_polls bounds the loop (default 40 × 3s ≈ 2 min) so a wedged generation —
// e.g. the worker dying without marking the record failed — stops polling
// instead of GETting forever for the life of the tab.
export default function AiDebrief({ endpoint, poll_interval = 3000, max_polls = 40 }) {
  const [state, setState] = useState({ status: "loading" });
  const timer = useRef(null);
  const polls = useRef(0);

  useEffect(() => {
    let active = true;
    polls.current = 0;

    async function load() {
      try {
        const res = await fetch(endpoint, { headers: { Accept: "application/json" } });
        if (!res.ok) {
          if (active) setState({ status: "absent" });
          return;
        }
        const data = await res.json();
        if (!active) return;
        setState(data);
        if (data.status === "pending") {
          polls.current += 1;
          if (polls.current >= max_polls) {
            setState({ status: "absent" });
            return;
          }
          timer.current = setTimeout(load, poll_interval);
        }
      } catch {
        if (active) setState({ status: "absent" });
      }
    }

    load();
    return () => {
      active = false;
      clearTimeout(timer.current);
    };
  }, [endpoint, poll_interval, max_polls]);

  if (state.status === "absent" || state.status === "failed" || state.status === "loading") {
    return null;
  }

  return (
    <section style={styles.card}>
      <div style={styles.eyebrow}>
        <i className="fa-solid fa-sparkles" style={{ marginRight: 8 }} />
        AI debrief
      </div>

      {state.status === "ready" ? (
        <>
          <div style={styles.grid}>
            <Block label="What this shows" body={state.pattern} accent="var(--ink-900, #0b1420)" />
            <Block label="Fix tonight" body={state.fix_tonight} accent="var(--accent, #0b6b7a)" />
            <Block label="Skip for now" body={state.deprioritize} accent="var(--ink-500, #6b7480)" />
          </div>
          <p style={styles.disclaimer}>Generated from this attempt. A study aid, not medical advice.</p>
        </>
      ) : (
        <p style={styles.pending}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
          Analysing your attempt…
        </p>
      )}
    </section>
  );
}

function Block({ label, body, accent }) {
  if (!body) return null;
  return (
    <div style={styles.block}>
      <div style={{ ...styles.blockLabel, color: accent }}>{label}</div>
      <p style={styles.blockBody}>{body}</p>
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid var(--ink-200, #e3e6ec)",
    borderRadius: "12px",
    background: "var(--paper, #fff)",
    padding: "18px 20px",
    margin: "0 0 24px",
    fontFamily: "var(--ui, system-ui)",
  },
  eyebrow: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "var(--accent-ink, #0b6b7a)",
    marginBottom: "14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  },
  block: {},
  blockLabel: { fontSize: "12px", fontWeight: 700, marginBottom: "6px" },
  blockBody: { margin: 0, fontSize: "14px", lineHeight: 1.55, color: "var(--ink-700, #2b3440)" },
  pending: { margin: 0, fontSize: "14px", color: "var(--ink-500, #6b7480)" },
  disclaimer: { margin: "14px 0 0", fontSize: "11px", color: "var(--ink-400, #9aa3ad)" },
};
